import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { GainsUiCommonsModule } from '@gainsystems/gains-ui-commons';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartData, ChartService } from 'app/shared/services/chart-service/chart.service';
import { ForecastDataPoint, ForecastWidgetComponent } from '../../shared/components/forecast-widget/forecast-widget.component';
import { PeriodComparisonData } from '../../shared/components/enhanced-kpi-widget/enhanced-kpi-widget.component';
import { BreakdownItem } from '../../shared/components/group-breakdown-kpi-widget/group-breakdown-kpi-widget.component';
import { secureRandom } from 'app/shared/util/secure-random.util';
import { GaugeThreshold } from '../../shared/components/gauge-kpi-widget/gauge-kpi-widget.component';
import { ChartsSharedModule } from '../../shared/components/charts/charts-shared.module';
import { ThresholdChartData } from '../../shared/components/charts/threshold-chart/threshold-chart.component';
import { ThresholdPerformanceService } from 'app/shared/services/threshold-performance.service';
import { MLComparisonData } from '../../shared/components/charts/ml-comparison-chart/ml-comparison-chart.component';
import { MLComparisonService } from '../../shared/services/ml-comparison.service';
import { InventoryBreakdownService, InventoryBreakdownData } from '../../shared/services/inventory-breakdown.service';
import { FeatureFlagsService } from '../../shared/services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_LEAD_TIME_PREDICTION } from 'app/shared/services/feature-flags/feature-flags.constants';
import { Subscription } from 'rxjs';
import { GainsSharedModule } from 'app/shared/modules/gains-shared.module';

/**
 * Interface for historical sales data record
 */
interface HistoryRawRecord {
  /** ISO date string */
  date: string;
  /** Sales amount in dollars */
  sales_dollars: number;
  /** Stock Keeping Unit identifier */
  sku: string;
}

/**
 * Interface for performance on-hand inventory record
 */
interface PerfOnHandRecord {
  /** ISO date string */
  date: string;
  /** Stock Keeping Unit identifier */
  sku: string;
  /** On-hand inventory value in dollars */
  on_hand_dollars: number;
}

/**
 * Interface for inventory turnover widget input data
 */
interface InventoryTurnoverInputs {
  /** Stock Keeping Unit identifier */
  sku: string;
  /** Standard cost (optional, may not be used directly) */
  std_cost?: number;
  /** Historical sales data for at least 12 months */
  history_raw: HistoryRawRecord[];
  /** On-hand inventory data for at least past 3 months */
  perf_array_on_hand: PerfOnHandRecord[];
  /** Previous period inventory turns value (optional) */
  archivedPreviousTurns?: number | null;
}

/**
 * Interface for calculated inventory turnover results
 */
interface InventoryTurnoverResult {
  /** Current period inventory turns */
  currentTurns: number | null;
  /** Percentage change vs previous period */
  versusPercent: number | null;
  /** Previous period turns value */
  previousPeriodTurns: number | null;
}

/**
 * Home Screen Component
 *
 * This component displays the main dashboard with various widgets.
 */

@Component({
  selector: 'home-screen',
  imports: [
    CommonModule,
    GainsSharedModule,
    GainsUiCommonsModule,
    AngularSvgIconModule,
    NzRadioModule,
    NzToolTipModule,
    FormsModule,
    NzButtonModule,
    ReactiveFormsModule,
    HighchartsChartComponent,
    ChartsSharedModule
  ],
  templateUrl: './home-screen.component.html',
  standalone: true,
  styleUrl: './home-screen.component.scss'
})
export class HomeScreenComponent implements OnInit, OnDestroy {
  @ViewChild('test') high!: HighchartsChartComponent;
  @ViewChild('forecastWidget') forecastWidget!: ForecastWidgetComponent;
  showMenu = false;
  readonly FEATURE_FLAG_LTP = FEATURE_FLAG_LEAD_TIME_PREDICTION;
  WIDTH_MENU_TITLE = 'Chart Width';
  EXPORT_MENU_TITLE = 'Export as';
  chartOptions: Highcharts.Options = {};

  // Feature flags - cached to avoid repeated method calls in template
  isLtpFeatureEnabled = false;
  private readonly featureFlagsSubscription?: Subscription;

  // Days On Hand Widget
  daysOnHand: number | string = '--';

  // Inventory Turnover Widget
  inventoryTurnoverData: InventoryTurnoverInputs | null = null;
  inventoryTurnoverResult: InventoryTurnoverResult = {
    currentTurns: null,
    versusPercent: null,
    previousPeriodTurns: null
  };

  // Inventory Investment Pie Chart KPI Widget
  inventoryInvestmentCurrent = { percentage: 52, label: 'Current Service Level' };
  inventoryInvestmentTarget = { percentage: 90, label: 'Company Target Service Level' };

  // Inventory Investment by Location - Grouped Breakdown KPI Widget
  inventoryLocationTotalValue = 0;
  inventoryLocationBreakdownItems: BreakdownItem[] = [];

  // Forecast Accuracy Widget
  forecastDataFrozenByMonth: { monthKey: string; value: number }[] = [];
  actualsByMonth: { monthKey: string; value: number }[] = [];
  forecastChart: Highcharts.Chart | undefined;
  forecastChartCallback: Highcharts.ChartCallbackFunction = chart => {
    this.forecastChart = chart;
  };
  forecastChartOptions: Highcharts.Options = {};

  // Forecast Widget Data
  forecastWidgetData: ForecastDataPoint[] = [];
  forecastWidgetWidthClass = 'chart-small';
  forecastAccuracyTooltip = 'Forecast Accuracy - Measures how closely actual demand matches the forecast that was generated at the start of each month for that same month.';

  // Threshold Performance Analysis Widget Data
  thresholdChartData: ThresholdChartData[] = [];
  thresholdChartWidthClass = 'chart-small';
  // ML vs Legacy Comparison Widget Data
  mlComparisonData: MLComparisonData | null = null;
  mlComparisonWidthClass = 'chart-small';

  exportData = [
    ['Item Class', '2024', '2025'],
    ['A', 107, 133],
    ['B', 31, 156],
    ['C', 635, 947],
    ['Others', 234, 654]
  ];

  chartWidgetWidthClass = 'chart-small'; // default width for chart widget
  kpiWidgetWidthClass = 'extra-chart-small'; // default width for KPI widget
  ellipsisPositionWithWidth = 'ellipsis-small';

  mlPerformanceWidthClass = 'chart-medium';

  // Gauge KPI Widget Data
  exceptionsValue = 374;
  exceptionsThresholds: GaugeThreshold[] = [
    { min: 0, max: 200, color: '#7BA428', label: 'Good' },
    { min: 201, max: 400, color: '#CC9E00', label: 'Warning' },
    { min: 401, max: 600, color: '#c00016', label: 'Critical' }
  ];

  gaugeWidgetWidthClass = 'extra-chart-small';

  constructor(
    private readonly _thresholdPerformanceService: ThresholdPerformanceService,
    private readonly chartService: ChartService,
    private readonly mlComparisonService: MLComparisonService,
    private readonly inventoryBreakdownService: InventoryBreakdownService,
    private readonly featureFlagsService: FeatureFlagsService
  ) {
    // Subscribe to feature flags to cache their values
    this.featureFlagsSubscription = this.featureFlagsService.featureFlags$.subscribe(flags => {
      this.isLtpFeatureEnabled = this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_LEAD_TIME_PREDICTION);
    });
  }

  ngOnDestroy(): void {
    this.featureFlagsSubscription?.unsubscribe();
  }

  /**
   * Get date that is 12 months ago from the reference date
   */
  private getTwelveMonthsAgo(referenceDate: Date = new Date()): Date {
    const date = new Date(referenceDate);
    date.setFullYear(date.getFullYear() - 1);
    return date;
  }

  /**
   * Get date that is 3 months ago from the reference date
   */
  private getThreeMonthsAgo(referenceDate: Date = new Date()): Date {
    const date = new Date(referenceDate);
    date.setMonth(date.getMonth() - 3);
    return date;
  }

  /**
   * Sum sales dollars for a specific SKU from the past 12 months
   */
  private sumPast12MonthsSales(historyRaw: HistoryRawRecord[], sku: string, referenceDate: Date = new Date()): number {
    const twelveMonthsAgo = this.getTwelveMonthsAgo(referenceDate);

    return historyRaw
      .filter(record => record.sku === sku && new Date(record.date) >= twelveMonthsAgo && new Date(record.date) <= referenceDate)
      .reduce((sum, record) => sum + record.sales_dollars, 0);
  }

  /**
   * Compute 3-month average of on-hand dollars for a specific SKU
   */
  private computeThreeMonthAvgOnHand(perfOnHand: PerfOnHandRecord[], sku: string, referenceDate: Date = new Date()): number {
    const threeMonthsAgo = this.getThreeMonthsAgo(referenceDate);

    const relevantRecords = perfOnHand.filter(record => record.sku === sku && new Date(record.date) >= threeMonthsAgo && new Date(record.date) <= referenceDate);

    if (relevantRecords.length === 0) {
      return 0;
    }

    const totalOnHand = relevantRecords.reduce((sum, record) => sum + record.on_hand_dollars, 0);
    return totalOnHand / relevantRecords.length;
  }

  /**
   * Calculate inventory turnover rate
   */
  private calculateInventoryTurns(past12MonthsSales: number, threeMonthAvgOnHand: number): number | null {
    if (threeMonthAvgOnHand === 0 || isNaN(threeMonthAvgOnHand) || isNaN(past12MonthsSales)) {
      return null;
    }

    return past12MonthsSales / threeMonthAvgOnHand;
  }

  /**
   * Calculate percentage change between current and previous period turns
   */
  private calculateVersusPercent(currentTurns: number | null, previousTurns: number | null): number | null {
    if (currentTurns === null || previousTurns === null || previousTurns === 0 || isNaN(currentTurns) || isNaN(previousTurns)) {
      return null;
    }

    return ((currentTurns - previousTurns) / previousTurns) * 100;
  }

  /**
   * Main function to calculate all inventory turnover metrics
   */
  private calculateInventoryTurnoverMetrics(data: InventoryTurnoverInputs): InventoryTurnoverResult {
    const past12MonthsSales = this.sumPast12MonthsSales(data.history_raw, data.sku);
    const threeMonthAvgOnHand = this.computeThreeMonthAvgOnHand(data.perf_array_on_hand, data.sku);

    const currentTurns = this.calculateInventoryTurns(past12MonthsSales, threeMonthAvgOnHand);

    const versusPercent = this.calculateVersusPercent(currentTurns, data.archivedPreviousTurns || null);

    return {
      currentTurns,
      versusPercent,
      previousPeriodTurns: data.archivedPreviousTurns || null
    };
  }

  /**
   * Format number to one decimal place
   */
  formatToOneDecimal(value: number | null): string {
    if (value === null || isNaN(value)) {
      return '—';
    }
    return value.toFixed(1);
  }

  /**
   * Format percentage value with proper sign and one decimal place
   */
  formatPercentage(value: number | null): string {
    if (value === null || isNaN(value)) {
      return '—';
    }
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  }

  get currentTurns(): number | null {
    return this.inventoryTurnoverResult.currentTurns;
  }

  get versusPercent(): number | null {
    return this.inventoryTurnoverResult.versusPercent;
  }

  get previousPeriodTurns(): number | null {
    return this.inventoryTurnoverResult.previousPeriodTurns;
  }

  get formattedCurrentTurns(): string {
    return this.formatToOneDecimal(this.currentTurns);
  }

  get formattedVersusPercent(): string {
    return this.formatPercentage(this.versusPercent);
  }

  get formattedPreviousPeriodTurns(): string {
    return this.formatToOneDecimal(this.previousPeriodTurns);
  }

  get inventoryKpiValueClass(): string {
    if (this.currentTurns === null) {
      return 'kpi-value';
    }
    return 'kpi-value kpi-value--positive';
  }

  get inventoryDeltaClass(): string {
    if (this.versusPercent === null) {
      return 'period-row__value';
    }

    return this.versusPercent >= 0 ? 'period-row__value delta--positive' : 'period-row__value delta--negative';
  }

  Math = Math;

  get hasInventoryTurnoverData(): boolean {
    return this.inventoryTurnoverData !== null && this.inventoryTurnoverData.history_raw.length > 0 && this.inventoryTurnoverData.perf_array_on_hand.length > 0;
  }

  get inventoryComparisonData(): PeriodComparisonData | null {
    if (!this.hasInventoryTurnoverData) {
      return null;
    }

    return {
      versusPercent: this.versusPercent,
      previousPeriodValue: this.previousPeriodTurns,
      formattedVersusPercent: this.formattedVersusPercent,
      formattedPreviousPeriodValue: this.formattedPreviousPeriodTurns,
      versusPercentClass: this.inventoryDeltaClass
    };
  }

  // mock values for onHand and forecast
  private readonly mockOnHand = 1200;
  private readonly mockHistFcstArray = [100, 110, 95, 105, 120, 115, 130, 125, 110, 100, 105, 115];

  ngOnInit() {
    this.chartService.fetch().subscribe(data => {
      this.updateChartOptions(data);
    });

    // Calculate Days On Hand for KPI widget
    const onHand = this.mockOnHand;
    const annualForecast = this.mockHistFcstArray.reduce((sum, v) => sum + v, 0);
    this.daysOnHand = annualForecast > 0 ? Math.round((onHand / annualForecast) * 365) : '--';

    this.initializeInventoryTurnoverData();
  }

  /**
   * Initialize mock data for inventory turnover widget
   */
  private initializeInventoryTurnoverData(): void {
    const currentDate = new Date();
    const history_raw: HistoryRawRecord[] = [];
    const perf_array_on_hand: PerfOnHandRecord[] = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() - i);
      history_raw.push({
        date: date.toISOString(),
        sales_dollars: 10000,
        sku: 'SKU123'
      });
    }

    for (let i = 2; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() - i);
      perf_array_on_hand.push({
        date: date.toISOString(),
        sku: 'SKU123',
        on_hand_dollars: 4800
      });
    }

    this.inventoryTurnoverData = {
      sku: 'SKU123',
      std_cost: 25.5,
      history_raw,
      perf_array_on_hand,
      archivedPreviousTurns: 26.0
    };

    if (this.inventoryTurnoverData) {
      this.inventoryTurnoverResult = this.calculateInventoryTurnoverMetrics(this.inventoryTurnoverData);
    }

    // Initialize Forecast Accuracy data
    this.initializeForecastAccuracyData();

    // Initialize Threshold Performance Analysis data
    this.initializeThresholdChartData();
    // Initialize ML vs Legacy Comparison data with loading simulation
    // Initialize Inventory Breakdown data
    this.loadInventoryBreakdownData();
    this.loadMLComparisonData();
  }

  updateChartOptions(data: ChartData[]) {
    const categories = data.map(d => d.type);
    const values = data.map(d => d.total);

    this.chartOptions = {
      chart: { type: 'bar', plotBorderWidth: 1 },
      title: { text: '' },
      xAxis: {
        categories: categories,
        title: { text: 'Item Class' },
        labels: { enabled: false }
      },
      yAxis: {
        min: 0,
        tickInterval: 1000,
        title: { text: 'On-Hand $', align: 'middle' },
        labels: {
          formatter: function () {
            return (this.value as number) > 0 ? (this.value as number) / 1000 + 'k' : this.value + '';
          }
        }
      },
      tooltip: {
        formatter: function () {
          return `On-Hand: ${this.y} $`;
        }
      },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: {
        bar: {
          borderRadius: 6,
          dataLabels: {
            enabled: true,
            align: 'left',
            inside: true,
            y: -20,
            verticalAlign: 'top',
            formatter: function () {
              return (this as any).point.category;
            },
            style: { color: '#52667A' }
          },
          colorByPoint: true
        }
      },
      colors: ['#1F290A', '#5C7B1E', '#7BA428', '#DEDEDE'],
      series: [
        {
          type: 'bar',
          data: values
        }
      ],
      exporting: {
        enabled: false,
        chartOptions: {
          // specific options for the exported image
          plotOptions: {
            series: {
              dataLabels: {
                enabled: true
              }
            }
          }
        },
        fallbackToExportServer: false
      }
    };
  }

  ellipsisDataItems: EllipsisMenuItem[] = [
    {
      title: 'Edit',
      icon: 'pen.svg',
      disabled: true
    },
    {
      title: 'Chart Width',
      icon: 'arrows-expand-x.svg',
      submenu: [
        { title: 'Small', id: 'small' },
        { title: 'Medium', id: 'medium' },
        { title: 'Large', id: 'large' },
        { title: 'Full Width', id: 'full' }
      ],
      disabled: true
    },
    {
      title: 'Export as',
      icon: 'download.svg',
      submenu: [{ title: 'CSV' }, { title: 'Excel' }, { title: 'PDF' }, { title: 'HTML' }]
    },
    {
      title: 'Delete',
      divider: true,
      icon: 'trash-2.svg',
      warning: true,
      disabled: true
    }
  ];

  openPopup = false;

  handleClick() {
    console.log('clicked');
  }

  filter() {
    // filter logic
    console.log('Filter Button Clicked');
  }

  personalize() {
    // personalize logic
    console.log('Personalize Button Clicked');
  }

  onExport(type: string): void {
    // Export functionality is now handled by individual widget components
    console.log(`Export ${type} requested - handled by widget components`);
  }

  // Export functions are now handled by individual widget components

  download(blob: Blob, name: string) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  }

  setChartWidth(
    size: string,

    widget: 'chart' | 'doh' | 'inventory-turnover' | 'forecast' | 'threshold' | 'ml-comparison' | 'ml-performance' | 'inventory-investment' | 'inventory-location' | 'exceptions'
  ): void {
    const widthClass = `chart-${size.toLowerCase()}`;
    if (widget === 'chart') {
      this.chartWidgetWidthClass = widthClass;
      setTimeout(() => {
        Highcharts.charts.forEach(chart => chart?.reflow());
      }, 100);
    } else if (widget === 'doh' || widget === 'inventory-turnover' || widget === 'inventory-investment' || widget === 'inventory-location') {
      this.kpiWidgetWidthClass = widthClass;
    } else if (widget === 'forecast') {
      this.forecastWidgetWidthClass = widthClass;
      setTimeout(() => {
        this.forecastWidget?.reflow();
      }, 100);
    } else if (widget === 'threshold' || widget === 'ml-comparison') {
      if (widget === 'threshold') {
        this.thresholdChartWidthClass = widthClass;
      } else if (widget === 'ml-comparison') {
        this.mlComparisonWidthClass = widthClass;
      }
      setTimeout(() => {
        Highcharts.charts.forEach(chart => chart?.reflow());
      }, 100);
    } else if (widget === 'ml-performance') {
      this.mlPerformanceWidthClass = widthClass;
      setTimeout(() => {
        Highcharts.charts.forEach(chart => chart?.reflow());
      }, 100);
    } else if (widget === 'exceptions') {
      this.gaugeWidgetWidthClass = widthClass;
    }
    setTimeout(() => {
      Highcharts.charts.forEach(chart => chart?.reflow());
    }, 100);
  }

  handleMenuAction(
    action: string,

    widget: 'chart' | 'doh' | 'inventory-turnover' | 'inventory-investment' | 'inventory-location' | 'forecast' | 'threshold' | 'ml-comparison' | 'ml-performance' | 'exceptions'
  ): void {
    const normalizedAction = action.toLowerCase();
    const widthItems = this.getSubmenuTitles(this.WIDTH_MENU_TITLE);
    const exportItems = this.getSubmenuTitles(this.EXPORT_MENU_TITLE);

    if (widthItems.includes(normalizedAction)) {
      this.setChartWidth(normalizedAction, widget);
    } else if (exportItems.includes(normalizedAction) && (widget === 'chart' || widget === 'forecast' || widget === 'threshold')) {
      this.onExport(normalizedAction);
    } else {
      console.warn(`Unhandled action: ${action}`);
    }
  }

  private getSubmenuTitles(parentTitle: string): string[] {
    const parent = this.ellipsisDataItems.find(item => item.title.toLowerCase() === parentTitle.toLowerCase());
    return parent?.submenu?.map(sub => sub.title.toLowerCase()) || [];
  }

  private generateStyledHTML(table: string): string {
    return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>Exported Chart Data</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          table {
            border-collapse: collapse;
            width: auto;
          }
          th, td {
            border: 1px solid black;
            padding: 8px 12px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
          }
        </style>
      </head>
      <body>
        ${table}
      </body>
    </html>
  `.trim();
  }

  // Forecast Accuracy Helper Functions
  private computeRolling12Months(currentPlanPeriod?: Date): string[] {
    const baseDate = currentPlanPeriod || new Date();
    const result: string[] = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(baseDate);
      date.setMonth(date.getMonth() - i);
      result.push(this.normalizeMonthKey(date));
    }

    return result;
  }

  private normalizeMonthKey(dateOrKey: string | Date): string {
    if (typeof dateOrKey === 'string') {
      if (/^\d{4}-\d{2}$/.exec(dateOrKey)) {
        return dateOrKey; // Already in YYYY-MM format
      }
      if (/^\d{4}-\d{2}-\d{2}/.exec(dateOrKey)) {
        return dateOrKey.substring(0, 7); // Extract YYYY-MM from YYYY-MM-DD
      }
    }

    const date = typeof dateOrKey === 'string' ? new Date(dateOrKey) : dateOrKey;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  private getValueForMonth(series: { monthKey: string; value: number }[], monthKey: string): number | null {
    const item = series.find(s => s.monthKey === monthKey);
    return item && !isNaN(item.value) ? item.value : null;
  }

  private determineYAxisMax(values: number[]): number {
    if (values.length === 0) return 100;
    const max = Math.max(...values);
    return max * 1.12; // 12% padding
  }

  private chooseNiceTickInterval(maxValue: number): number {
    const intervals = [1, 5, 10, 50, 100, 500, 1000, 5000, 10000];
    const targetTicks = 5;
    const roughInterval = maxValue / targetTicks;

    // Find the best interval
    for (const interval of intervals) {
      if (interval >= roughInterval) {
        return interval;
      }
    }

    // For very large values, use power of 10 scaling
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
    return Math.ceil(roughInterval / magnitude) * magnitude;
  }

  private initializeForecastAccuracyData(): void {
    // Mock data
    const months = this.computeRolling12Months();

    this.forecastWidgetData = months.map((month, index) => ({
      monthKey: month,
      actualValue: 110 + secureRandom() * 180 + index * 8,
      forecastValue: 100 + secureRandom() * 200 + index * 10
    }));
  }

  /**
   * Initialize mock data for Threshold Performance Analysis widget
   * Data shows the relationship between ML performance threshold and adoption/accuracy
   */
  private initializeThresholdChartData(): void {
    // Generate data for thresholds from 0.0 to 0.95 in 0.05 increments
    this.thresholdChartData = this._thresholdPerformanceService.getThresholdPerformanceChartData();
  }

  /**
   * Load ML vs Legacy Comparison data using the service
   */
  private loadMLComparisonData(): void {
    this.mlComparisonService.getMLComparisonData().subscribe({
      next: (data: MLComparisonData) => {
        this.mlComparisonData = data;
      }
    });
  }

  /**
   * Load Inventory Breakdown data using the service
   */
  private loadInventoryBreakdownData(): void {
    this.inventoryBreakdownService.getInventoryBreakdownData().subscribe({
      next: (data: InventoryBreakdownData) => {
        this.inventoryLocationTotalValue = data.totalValue;
        this.inventoryLocationBreakdownItems = data.breakdownItems;
      }
    });
  }

  /**
   * Extract size from width class (e.g., 'chart-large' -> 'large')
   * For ml-performance-distribution: full/large -> 'large', medium/small -> 'medium'
   */
  getWidgetSize(widthClass: string): 'large' | 'medium' | 'small' {
    if (widthClass.includes('large') || widthClass.includes('full')) return 'large';
    if (widthClass.includes('medium') || widthClass.includes('small')) return 'medium';
    return 'medium';
  }
}
